// Global Decision Engine — clarification logic only. All scoring/ranking is server-side.

import { parseQuery, type ParsedQuery } from "./queryParser";
import { getSourcesForCategory } from "./sourceModel";
import { loadMemory, getClarificationReductionLevel } from "./userMemory";

// ─── Types ───────────────────────────────────────────────────────────────────

export type ConfidenceLevel = "high" | "medium" | "low" | "insufficient";

export interface AuditData {
  whyWon: string[];
  whyOthersLost: string[];
  filteredOut: string[];
  scope: string;
  sourcesAnalyzed: number;
  offersCompared: number;
  optionsFiltered: number;
  dataConsistency: "high" | "medium" | "low";
  lastChecked: string;
  retrievalSummary: string;
  eliminationReasons?: string[];
}

export interface VerifiedOfferDisplay {
  retailer: string;
  price: string;
  priceUSD: number;
  url: string;
  trustScore: number;
  delivery: string;
  isVerified: boolean;
  isDomestic: boolean;
  landedCost: {
    productPrice: number;
    shippingCost: number;
    taxEstimate: number;
    dutyEstimate: number;
    totalLanded: number;
    currency: string;
    isDomestic: boolean;
    breakdown: string;
  };
  linkType?: "product-page" | "search-page" | "retailer-visit";
}

export interface DecisionOption {
  id: string;
  label: "Recommended" | "Best Value" | "Fastest Delivery" | "Premium Pick";
  tier: "A" | "B" | "C";
  productName: string;
  verdict: string;
  explanation: string;
  tradeoff: string;
  scores: { price: number; delivery: number; trust: number; quality: number };
  compositeScore: number;
  dominanceGap: number;
  price: string;
  deliveryTime: string;
  buyUrl?: string;
  confidence: ConfidenceLevel;
  audit: AuditData;
  freshness: "fresh" | "recent" | "stale";
  verifiedOffers?: VerifiedOfferDisplay[];
  landedCost?: { totalLanded: number; currency: string; breakdown: string };
  verification?: "verified" | "partially-verified" | "ai-estimated";
  priceVerified?: boolean;
  bestVerifiedPrice?: number;
}

export interface ClarificationQuestion {
  id: string;
  question: string;
  options: string[];
}

export type CoverageState = "live" | "partial" | "demo";

export interface TrustProof {
  offersChecked: number;
  sourcesChecked: number;
  removed: number;
  removalReasons: string[];
  market: string;
  candidatesRetrieved: number;
  retrievalModes: string[];
}

export interface DecisionResult {
  parsed: ParsedQuery;
  clarifications?: ClarificationQuestion[];
  decisions?: DecisionOption[];
  coverage: CoverageState;
  trustProof: TrustProof;
}

// ─── Clarification Logic ─────────────────────────────────────────────────────

const CLARIFICATION_RULES: Record<string, ClarificationQuestion[]> = {
  "office chair": [
    { id: "oc1", question: "What matters most to you?", options: ["Comfort & ergonomics", "Lowest price", "Fastest delivery", "Premium build quality"] },
    { id: "oc2", question: "What's your budget range?", options: ["Under $200", "$200-$400", "$400+"] },
  ],
  monitor: [
    { id: "mon1", question: "What will you use it for?", options: ["Work & productivity", "Gaming", "Creative & color work"] },
    { id: "mon2", question: "What matters most?", options: ["Lowest price", "Best image quality", "Fastest refresh rate"] },
  ],
  vacuum: [
    { id: "vac1", question: "What matters most?", options: ["Pet hair handling", "Quiet operation", "Maximum power", "Lowest price"] },
  ],
  headphones: [
    { id: "hp1", question: "What's your priority?", options: ["Best noise cancellation", "Longest battery life", "Lowest price", "Best sound quality"] },
  ],
  earbuds: [
    { id: "eb1", question: "What's your priority?", options: ["Noise cancellation", "Workout/sweat proof", "Lowest price", "Best sound"] },
  ],
  "espresso machine": [
    { id: "esp1", question: "What type of espresso maker?", options: ["Fully automatic", "Semi-automatic", "Pod/capsule", "Recommend for me"] },
  ],
  "protein powder": [
    { id: "pp1", question: "Any dietary preference?", options: ["Whey protein", "Plant-based / vegan", "No preference"] },
  ],
  "air fryer": [
    { id: "af1", question: "What size do you need?", options: ["Small (1-2 people)", "Medium (3-4 people)", "Large (5+ people)"] },
  ],
  tv: [
    { id: "tv1", question: "Primary use?", options: ["Movies & streaming", "Sports", "Gaming", "General use"] },
  ],
  // ─── New categories ────────────────────────────────────────────────
  skincare: [
    { id: "sk1", question: "What's your skin concern?", options: ["Anti-aging", "Acne & breakouts", "Hydration", "General routine"] },
  ],
  vitamins: [
    { id: "vit1", question: "What's the goal?", options: ["General wellness", "Immune support", "Energy & focus", "Sleep & recovery"] },
  ],
  supplements: [
    { id: "sup1", question: "What's the goal?", options: ["Muscle & recovery", "General health", "Energy & focus", "Gut health"] },
  ],
  "dog food": [
    { id: "df1", question: "What matters most?", options: ["Best ingredients", "Lowest price", "Specific dietary need", "My vet recommended"] },
  ],
  "cat food": [
    { id: "cf1", question: "What matters most?", options: ["Best ingredients", "Lowest price", "Wet vs dry", "Sensitive stomach"] },
  ],
  strollers: [
    { id: "str1", question: "What type do you need?", options: ["Everyday / full-size", "Lightweight travel", "Jogging", "Double / tandem"] },
  ],
  "power tools": [
    { id: "pt1", question: "Experience level?", options: ["DIY beginner", "Experienced DIYer", "Professional use"] },
  ],
  "running shoes": [
    { id: "rs1", question: "Primary use?", options: ["Road running", "Trail running", "Gym & training", "Casual daily wear"] },
  ],
  mattress: [
    { id: "mat1", question: "What's your preference?", options: ["Firm support", "Soft & plush", "Medium / balanced", "Cooling"] },
  ],
  luggage: [
    { id: "lug1", question: "What size?", options: ["Carry-on only", "Checked / large", "Full set"] },
  ],
  coffee: [
    { id: "cof1", question: "What type?", options: ["Whole bean", "Ground", "Pods / capsules", "Instant"] },
  ],
  // ─── Scale-Up: deeper clarification rules ─────────────────────────────
  "hair care": [
    { id: "hc1", question: "What's your hair concern?", options: ["Damage repair", "Volume & thickness", "Color-treated", "Dandruff & scalp"] },
  ],
  makeup: [
    { id: "mk1", question: "What are you looking for?", options: ["Foundation & base", "Eye makeup", "Lip products", "Full routine"] },
  ],
  fragrance: [
    { id: "fr1", question: "What occasion?", options: ["Daily wear", "Date night", "Office / subtle", "Gift for someone"] },
  ],
  "pet accessories": [
    { id: "pa1", question: "What type of pet?", options: ["Dog", "Cat", "Other"] },
  ],
  "baby gear": [
    { id: "bg1", question: "What age range?", options: ["Newborn (0-6mo)", "Infant (6-12mo)", "Toddler (1-3yr)"] },
  ],
  "hand tools": [
    { id: "ht1", question: "What project?", options: ["General home repair", "Plumbing", "Electrical", "Woodworking"] },
  ],
  "household cleaners": [
    { id: "hcl1", question: "What surface?", options: ["Kitchen", "Bathroom", "Floors", "All-purpose"] },
  ],
};

function needsClarification(parsed: ParsedQuery): ClarificationQuestion[] | undefined {
  if (parsed.retrieval.exactMatches > 0) {
    const topConfidence = Math.max(...parsed.retrieval.candidates.map(c => c.retrievalConfidence));
    if (topConfidence >= 0.8) return undefined;
  }

  const mem = loadMemory();
  const reductionLevel = getClarificationReductionLevel(mem);
  if (reductionLevel === "full") return undefined;

  const hasEnoughContext = parsed.priorities.length >= 2 || (parsed.budget && parsed.urgency) || !!parsed.useCase;
  if (hasEnoughContext) return undefined;

  const questions = CLARIFICATION_RULES[parsed.productType];
  if (!questions) return undefined;

  const relevant = questions.filter((q) => {
    if (q.id.endsWith("2") && q.question.toLowerCase().includes("budget") && parsed.budget) return false;
    if (q.question.toLowerCase().includes("use") && parsed.useCase) return false;
    if (q.question.toLowerCase().includes("priority") && mem.preferredPriority) return false;
    return true;
  });

  const maxQuestions = reductionLevel === "partial" ? 1 : 2;
  return relevant.length > 0 ? relevant.slice(0, maxQuestions) : undefined;
}

// ─── Trust Proof Generator (Minimal for Clarification Flow) ─────────────────

function generateTrustProof(parsed: ParsedQuery): TrustProof {
  const market = parsed.region || "US";
  const sources = getSourcesForCategory(parsed.productType, market);
  const retrievalModes: string[] = [];
  if (parsed.retrieval.exactMatches > 0) retrievalModes.push("exact");
  if (parsed.retrieval.phraseMatches > 0) retrievalModes.push("phrase");
  if (parsed.retrieval.semanticMatches > 0) retrievalModes.push("semantic");

  return {
    offersChecked: 0,
    sourcesChecked: sources.length,
    removed: 0,
    removalReasons: [],
    market: parsed.country || market,
    candidatesRetrieved: parsed.retrieval.candidates.length,
    retrievalModes,
  };
}

// ─── Public API ──────────────────────────────────────────────────────────────

export function runDecisionEngine(rawQuery: string): DecisionResult {
  const parsed = parseQuery(rawQuery);
  const trustProof = generateTrustProof(parsed);

  const clarifications = needsClarification(parsed);
  if (clarifications) {
    return { parsed, clarifications, coverage: "demo", trustProof };
  }

  return { parsed, coverage: "demo", trustProof };
}

export function runDecisionAfterClarification(rawQuery: string): DecisionResult {
  const parsed = parseQuery(rawQuery);
  const trustProof = generateTrustProof(parsed);
  return { parsed, coverage: "demo", trustProof };
}
